import { PageHero } from '../components/ui/PageHero.jsx';
import { Section, Wrap, SectionIntro, Grid } from '../components/ui/Section.jsx';
import { Reveal } from '../components/ui/motion.jsx';
import { Eyebrow } from '../components/ui/Eyebrow.jsx';
import { Pillar } from '../components/ui/Pillar.jsx';
import { Card } from '../components/ui/Card.jsx';
import { Button } from '../components/ui/Button.jsx';
import { CtaBanner } from '../components/ui/CtaBanner.jsx';
import { site, contact } from '../data/site.js';
import { values } from '../data/content.js';
import styles from './AboutPage.module.css';

export const meta = {
  title: 'About — Abode Custom Homes',
  description:
    'Abode Custom Homes was founded in 2026 in Bloomington, Illinois, on a simple belief: a custom home should actually be custom.',
};

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="About Abode"
        lines={['A new builder, built on', 'an old idea: care.']}
        lede="Abode Custom Homes was founded in 2026 right here in Bloomington, Illinois — built around a simple belief that a custom home should actually be custom, and the people building it should actually show up."
      />

      <Section>
        <Wrap>
          <Grid cols={2} className={styles.centered}>
            <Reveal>
              <Eyebrow animated={false}>Our Story</Eyebrow>
              <h2 className="h2 mt-2">Why we started Abode</h2>
              <p className="lede lede--wide mt-3">
                Too many custom home builders in central Illinois treat &ldquo;custom&rdquo; as a
                marketing word, not a promise. We started Abode to do it differently: small enough
                that you always know who&rsquo;s building your home, disciplined enough to hit the
                budget and timeline we agree on.
              </p>
              <p className="lede lede--wide mt-3">
                We&rsquo;re proudly based in Bloomington, and we&rsquo;re building our first
                projects across McLean County — from in-town lots to acreage on the edge of town.
                Every home carries the same standard, whether it&rsquo;s our first or our fiftieth.
              </p>
            </Reveal>

            <Reveal className={styles.figure}>
              <img
                src="/images/rendering-01.jpg"
                alt="Modern farmhouse custom home built by Abode Custom Homes."
                loading="lazy"
              />
            </Reveal>
          </Grid>
        </Wrap>
      </Section>

      <Section tone="alt">
        <Wrap>
          <Reveal as={SectionIntro}>
            <div>
              <Eyebrow animated={false}>What We Value</Eyebrow>
              <h2 className="h2 mt-2">The standard behind<br />every Abode home.</h2>
            </div>
          </Reveal>

          <Grid cols={3}>
            {values.map((value) => (
              <Reveal key={value.num}>
                <Pillar num={value.num} title={value.title} drawn={false}>
                  {value.body}
                </Pillar>
              </Reveal>
            ))}
          </Grid>
        </Wrap>
      </Section>

      <Section>
        <Wrap>
          <Grid cols={2}>
            <Reveal>
              <Eyebrow animated={false}>Service Area</Eyebrow>
              <h2 className="h2 mt-2">Bloomington &amp;<br />McLean County</h2>
              <p className="lede lede--wide mt-3">
                We build custom homes throughout Bloomington-Normal and the surrounding McLean
                County communities. If you&rsquo;re not sure whether your lot is in range, just
                ask — we&rsquo;re happy to take a look.
              </p>
            </Reveal>

            <Reveal as={Card}>
              <Eyebrow animated={false}>Get In Touch</Eyebrow>
              <p className="mt-3">
                <strong>Phone</strong><br />
                <a className="underlined" href={contact.phoneHref}>{contact.phone}</a>
              </p>
              <p className="mt-3">
                <strong>Email</strong><br />
                <a className="underlined" href={contact.emailHref}>{contact.email}</a>
              </p>
              <p className="mt-3"><strong>Location</strong><br />{site.location}</p>
              <Button to="/contact" className="mt-4">Contact Us</Button>
            </Reveal>
          </Grid>
        </Wrap>
      </Section>

      <Section tone="dark">
        <Wrap>
          <CtaBanner
            eyebrow="Ready When You Are"
            lines={["Let's build your", 'Abode.']}
            action="Start a Conversation"
            onDark
          />
        </Wrap>
      </Section>
    </>
  );
}
